import { StyleFn } from '..'

export const RangeSlider: StyleFn = ({ color, elevation, font, opacity, size }) => {
  const baseTickStyle = {
    userSelect: 'none',
    position: 'absolute',
    transform: `translate(-50%, ${size.spacing[16]})`,
    color: color.type === 'light' ? color.text.primary.onWhite : color.text.primary.onDark,
    fontSize: font.size.small,
    lineHeight: font.size.small,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: size.spacing[6],
      height: size.spacing[6],
      borderRadius: '50%',
      backgroundColor: color.type === 'light' ? color.palette.white : color.palette.coolgrey[40],
      top: `calc(-${size.spacing[16]} + 1px)`,
      left: 'calc(50% - 3px)',
    },
  }

  const baseThumbStyle = {
    position: 'absolute',
    width: size.spacing[16],
    height: size.spacing[16],
    borderRadius: '50%',
    border: `2px solid ${color.type === 'light' ? color.palette.white : color.background.primary.onDark}`,
    backgroundColor: color.type === 'light' ? color.brand.normal : color.palette.white,
    transform: 'translate(-50%, -25%)',
    boxShadow: elevation.level1,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: color.type === 'light' ? color.brand.dark : color.palette.coolgrey[10],
    },
    '&:focus, &:focus-visible': {
      outline: 'none',
    },
    '&:focus-visible:before': {
      content: "''",
      position: 'absolute',
      top: `calc(-${size.spacing[4]})`,
      left: `calc(-${size.spacing[4]})`,
      bottom: `calc(-${size.spacing[4]})`,
      right: `calc(-${size.spacing[4]})`,
      borderRadius: size.spacing[4],
      border: `2px solid ${color.getModalValues(color.border.focus)}`,
    },
  }

  return {
    root: {
      position: 'relative',
      width: '100%',
      display: 'block',
      boxSizing: 'border-box',
      minHeight: size.spacing[16],
      '&:before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: size.spacing[8],
        borderRadius: size.spacing[4],
        backgroundColor: color.type === 'light' ? color.palette.warmgrey[30] : color.palette.whiteTransparent[50],
      },
    },
    _vertical: {
      height: '100%',
      width: 'unset',
      display: 'inline-block',
      '&:before': {
        height: '100%',
        width: size.spacing[8],
      },
      '&$_withticks': {
        minWidth: size.spacing[32],
        minHeight: 'unset',
      },
      '& $track': {
        width: size.spacing[8],
        height: 'unset',
      },
      '& $tick': {
        transform: `translate(${size.spacing[16]}, -50%)`,
        '&:before': {
          top: size.spacing[4],
          left: `calc(-${size.spacing[16]} + 1px)`,
        },
      },
      '& $tick$tick_start': {
        transform: `translate(${size.spacing[16]}, -100%)`,
        '&:before': { top: 'unset', bottom: 1 },
      },
      '& $tick$tick_end': {
        transform: `translate(${size.spacing[16]}, 0)`,
        '&:before': { top: 1 },
      },
      '& $thumb': {
        transform: 'translate(-25%, -50%)',
      },
      '&$_large': {
        '& > $tick': {
          transform: `translate(${size.spacing[20]}, -50%)`,
          '&:before': {
            top: size.spacing[4],
            left: `calc(-${size.spacing[16]} + -3px)`,
          },
        },
        '& > $tick_start': {
          transform: `translate(${size.spacing[20]}, -100%)`,
          '&:before': { top: 'unset', bottom: 1 },
        },
        '& > $tick$tick_end': {
          transform: `translate(${size.spacing[20]}, 0)`,
          '&:before': { top: 1, left: `calc(-${size.spacing[16]} - 3px)` },
        },
        '& > $thumb': {
          transform: 'translate(-37%, -55%)',
        },
        '& $tooltip': {
          height: `calc(100% + ${size.spacing[20]})`,
          margin: -10,
        },
      },
    },
    _medium: {},
    _large: {
      '& > $tick': {
        extend: baseTickStyle,
        transform: `translate(-50%, calc(${size.spacing[20]} + 1px))`,
        '&:before': {
          extend: baseTickStyle['&:before'],
          top: `calc(-${size.spacing[20]})`,
        },
      },
      '& > $tick_start': {
        transform: `translate(14%, calc(${size.spacing[20]} + 1px))`,
        '&:before': { top: `calc(-${size.spacing[20]})` },
      },
      '& > $tick + $tick_end': {
        transform: `translate(-100%, calc(${size.spacing[20]} + 1px))`,
        '&:before': {
          left: 'unset',
          right: 1,
          top: `calc(-${size.spacing[20]})`,
        },
      },
      '& > $tick + $tick_outside': {
        '&:before': { backgroundColor: color.palette.coolgrey[40] },
      },
      '& > $thumb': {
        extend: baseThumbStyle,
        width: '2rem',
        height: '2rem',
        border: '3px solid white',
        transform: 'translate(-50%, -40%)',
      },
    },
    _disabled: {
      cursor: 'not-allowed',
      opacity: opacity.disabled,
      '& $track, & $thumb': {
        pointerEvents: 'none',
      },
    },
    _withticks: {
      minHeight: size.spacing[32],
    },
    track: {
      position: 'absolute',
      height: size.spacing[8],
      borderRadius: size.spacing[4],
      backgroundColor: color.type === 'light' ? color.brand.normal : color.palette.white,
    },
    tick: { ...baseTickStyle },
    tick_outside: {
      '&:before': { backgroundColor: color.palette.coolgrey[40] },
    },
    tick_start: {
      transform: `translate(0, ${size.spacing[16]})`,
      '&:before': { left: 1 },
    },
    tick_end: {
      transform: `translate(-100%, ${size.spacing[16]})`,
      '&:before': { left: 'unset', right: 1 },
    },
    tick_current: {
      fontWeight: font.weight.bold,
    },
    thumb: { ...baseThumbStyle },
    tooltip: {
      height: `calc(100% + ${size.spacing[4]})`,
      margin: -2,
    },
  }
}
